import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';

import {
  absagen,
  anlegen,
  alleZumArtikel,
  serieAnlegen,
  freigeben as freigebenTermin,
  standorte as ladeStandorte,
  verlegen,
  type Benachrichtigt,
  type Terminuebersicht,
} from '../api/termine';
import { liste as ladeArtikel, type Artikelzeile, type Produktstandort } from '../api/produkte';
import { liste as ladeAuftraege, type Auftragszeile } from '../api/auftraege';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt } from '../datum';
import { reservierungssatz, Terminfenster, vorgangsmeldung } from './Terminfenster';
import { Durchfuehrungsliste } from './Durchfuehrungsliste';
import { Dialog } from '../komponenten/Dialog';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Beginnwahl, ERSTE_RASTERZEIT } from '../komponenten/Beginnwahl';
import { Meldung } from '../komponenten/Meldung';
import { Terminkalender } from './Terminkalender';
import { Planung } from '../komponenten/Planung';
import { Segmente } from '../komponenten/Segmente';
import { useTelefon } from '../komponenten/telefon';
import type { Terminansicht } from '../wegweiser';

/**
 * Termine: Serien anlegen, verlegen, absagen (BUC-01, BUC-08, BUC-138, ÄA-04).
 *
 * ============================================================================
 * VERLEGEN UND ABSAGEN SIND NICHT DASSELBE
 * ============================================================================
 *
 * Sie stehen deshalb als zwei Handlungen da und nicht als eine mit einem
 * Schalter. An jeder hängt ein eigenes Recht (ÄA-04: „einen termin darf nur
 * die orga verschieben oder absagen"), und der Grund ist bei der Absage
 * Pflicht: Er steht im Anschreiben, und „Ihr Termin wurde abgesagt" ohne
 * Grund ist die Nachricht, nach der jeder Empfänger anruft.
 *
 * ============================================================================
 * DIE ARBEITSLISTE STEHT DA, AUCH WENN SIE LEER IST
 * ============================================================================
 *
 * BUC-138: „Alle Betroffenen werden in einem Vorgang benachrichtigt; wer der
 * Verlegung nicht zustimmt, erscheint in einer Arbeitsliste zur
 * Einzelbehandlung."
 *
 * Nach dem Verlegen zeigt die Maske, WER angeschrieben wurde — und wer nicht,
 * mit Grund. Das zu verschweigen hieße, dass ein Kunde ohne E-Mail-Adresse
 * still übergangen wird und am alten Datum vor der Tür steht.
 */

/** Der Zeitpunkt für ein `datetime-local`-Feld, in Ortszeit. */
function alsLokal(datum: Date): string {
  const versatz = datum.getTimezoneOffset() * 60_000;
  return new Date(datum.getTime() - versatz).toISOString().slice(0, 16);
}

/**
 * Das Terminraster (AeA-26): 45 Minuten, gezählt ab acht Uhr.
 *
 * `min` gibt dem Feld den Bezugspunkt für seine Stufen. Der Tag darin ist
 * gleichgültig — ein Kalendertag umfasst genau 32 Schritte, das Raster
 * wiederholt sich also an jedem Tag an derselben Stelle. Die verbindliche
 * Prüfung steht ohnehin in der Datenbank (0206); dieses Feld erspart dem
 * Anwender nur den Weg dorthin.
 */
/**
 * DAS FELD „ORT" FÜR EINZELTERMIN UND SERIE.
 *
 * EINE AUSWAHL UND KEIN FREITEXT. `termin.ort` trägt einen Fremdschlüssel auf
 * `standort.kennung`. Als Freitextfeld wies es jeden Tippfehler ab („Laatzen"
 * statt „LAATZEN"), und wer nichts eintrug, bekam einen Termin ohne Ort — der
 * gehört zu keinem Betriebstag und erscheint in keiner Tagesplanung. Am
 * 07.09.2026 standen sechs von sieben Terminen so da.
 *
 * DIE STANDORTE DES PRODUKTS (PRD-01, BUC-01 i. d. F. AeA-39, Entwurf,
 * Auftrag #22). Bei genau einem aktiven ist er vorbelegt, und die leere Wahl
 * heißt „wie das Produkt": Der Auslöser aus 0279 setzt ihn ohnehin. Bei
 * mehreren stehen genau diese oben zur Wahl, ohne Vorbelegung. Die übrigen
 * bleiben wählbar, denn ein Termin darf abweichen — aber nicht still: Die
 * Maske sagt, dass der Ort nicht zu den Standorten des Produkts gehört. Das
 * kann ein Versehen sein, und es soll auffallen, bevor der Termin im falschen
 * Tagesplan steht.
 */
function Ortwahl({
  kennung,
  wert,
  beiWahl,
  produktorte,
  standorte,
}: {
  readonly kennung: string;
  readonly wert: string;
  readonly beiWahl: (wert: string) => void;
  /** Die AKTIVEN Standorte des gewählten Produkts. */
  readonly produktorte: readonly Produktstandort[];
  /** Alle aktiven Standorte. */
  readonly standorte: readonly { readonly kennung: string; readonly bezeichnung: string }[];
}): JSX.Element {
  const fremd = wert !== '' && produktorte.length > 0 && !produktorte.some((s) => s.kennung === wert);
  const name = standorte.find((s) => s.kennung === wert)?.bezeichnung ?? wert;
  const option = (s: { readonly kennung: string; readonly bezeichnung: string }): JSX.Element => (
    <option key={s.kennung} value={s.kennung}>
      {s.bezeichnung}
    </option>
  );
  return (
    <div className="feld">
      <label className="feld__beschriftung" htmlFor={kennung}>
        Ort
      </label>
      <select
        id={kennung}
        className="feld__waehlen"
        value={wert}
        onChange={(e) => {
          beiWahl(e.target.value);
        }}
      >
        <option value="">{produktorte.length === 1 ? 'wie das Produkt' : 'ohne Ort'}</option>
        {produktorte.length === 0 ? (
          standorte.map(option)
        ) : (
          <>
            <optgroup label="Standorte des Produkts">{produktorte.map(option)}</optgroup>
            <optgroup label="Andere Standorte">
              {standorte.filter((s) => !produktorte.some((p) => p.kennung === s.kennung)).map(option)}
            </optgroup>
          </>
        )}
      </select>
      {fremd && (
        <p className="warnung" role="status">
          {name} gehört nicht zu den Standorten dieses Produkts (
          {produktorte.map((s) => s.bezeichnung).join(', ')}). Der Termin wird trotzdem dort
          angelegt.
        </p>
      )}
      {produktorte.length > 1 && wert === '' && (
        <p className="feld__hilfe">
          Das Produkt wird an mehreren Standorten angeboten. Wählen Sie, wo der Termin
          stattfindet.
        </p>
      )}
      {/* TAG-01 */}
      <p className="feld__hilfe">
        Ohne Ort gehört der Termin zu keinem Betriebstag und steht in keiner Tagesplanung.
      </p>
    </div>
  );
}

/**
 * Ein Vorschlag, der auf dem Raster liegt (ÄA-26).
 *
 * Die Maske schlägt beim Anlegen einen Zeitpunkt vor. Läge er daneben, zeigte
 * die Auswahl eine andere Zeit als die vorbelegte — und wer nichts ändert,
 * legte den Termin zu einer Uhrzeit an, die er nie gewählt hat.
 */
function rastervorschlag(inTagen: number): string {
  const d = new Date(Date.now() + inTagen * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-`
    + `${String(d.getDate()).padStart(2, '0')}T${ERSTE_RASTERZEIT}`;
}

/**
 * ÄW-DP-02, STA-01 (Tabelle 27, Zeilen 8 und 11): Verlegen und Absagen nur
 * aus geplant, buchbar und ausgebucht — wie `verlege()` in der API, die
 * alles andere mit 409 ablehnt.
 */
function verlegbar(status: string): boolean {
  return status === 'geplant' || status === 'buchbar' || status === 'ausgebucht';
}

export function Termine({
  darfAnlegen = false,
  darfVerlegen = false,
  darfAbsagen,
  darfVereinbarungAendern = false,
  darfWartelisteSehen = false,
  beiTerminakte,
  darfOptionBeantworten,
  ansicht,
  beiAnsicht,
  beiTagesplan,
}: {
  /**
   * Die gewählte Ansicht aus der Adresse (Auftrag CRM-67). Fehlt sie, führt
   * die Maske die Wahl selbst und beginnt im Kalender — auf dem Telefon bei
   * den Tagen (Auftrag CRM-106).
   */
  readonly ansicht?: Terminansicht;
  /** Meldet die Wahl, damit die Adresse sie trägt und ein Neuladen sie behält. */
  readonly beiAnsicht?: (ansicht: Terminansicht) => void;
  /**
   * Der Sprung in die Trainingsplanung mit einem Tag (TAG-29, Auftrag
   * CRM-67) — nur, wer `tagesplan/sehen` hat; sonst bleibt das Datum Text.
   */
  readonly beiTagesplan?: (tag: string, standort: string | undefined) => void;
  /**
   * SYS-01, BEN-02 (Auftrag #32): Termine und Serien anlegen — `termin:anlegen`. Wer Termine
   * nur sieht (Geschaeftsleitung, Vertrieb, Service, Empfang), bekommt die
   * Knoepfe nicht; die API wiese sie mit 403 ab.
   */
  readonly darfAnlegen?: boolean;
  /** ÄA-04, BEN-02 (Auftrag #32): den Termin verlegen — `termin:verlegen`. */
  readonly darfVerlegen?: boolean;
  /** ÄA-04, BUC-138: Die Absage schreibt jedem Gebuchten. */
  readonly darfAbsagen: boolean;
  /** DAT-08: die Exklusivvereinbarung pflegen — `termin/aendern`. */
  readonly darfVereinbarungAendern?: boolean;
  /** BUC-11 (ÄA-55): wie viele Wartende zum Termin passen — `buchung/sehen`. */
  readonly darfWartelisteSehen?: boolean;
  /** CRM-117 (L6): der Weg in die Terminakte; ohne ihn bleibt das Fenster ohne Verweis. */
  readonly beiTerminakte?: (termin: string) => void;
  /** ANG-32: Die Antwort auf eine Terminoption gibt die Disposition. */
  readonly darfOptionBeantworten: boolean;
}): JSX.Element {
  const [artikel, setzeArtikel] = useState<readonly Artikelzeile[]>([]);
  const [gewaehlt, setzeGewaehlt] = useState('');
  /*
   * TAG-29: Kalender, Tage oder Liste — umschaltbar, nicht untereinander (NFA-22, Auftrag CRM-67).
   *
   * AUF DEM TELEFON BEGINNT SIE BEI DEN TAGEN (Auftrag CRM-106, ÄA-63). Sieben
   * Spalten auf 390 px lassen der Zelle kaum 50 px — „Tage im Einzelnen" liest
   * sich dort besser. Es ist nur die Vorgabe: Die Wahl bleibt, und eine
   * Ansicht aus der Adresse gilt wie überall.
   */
  const telefon = useTelefon();
  const [eigeneSicht, setzeEigeneSicht] = useState<Terminansicht>(
    () => ansicht ?? (telefon ? 'tage' : 'kalender'),
  );
  const sicht = ansicht ?? eigeneSicht;
  const setzeSicht = (neu: Terminansicht): void => {
    setzeEigeneSicht(neu);
    beiAnsicht?.(neu);
  };
  /* Das Terminfenster — dieselbe Auskunft wie im Kalender und im Tagesplan. */
  const [offenerTermin, setzeOffenenTermin] = useState<string | null>(null);
  const [termine, setzeTermine] = useState<readonly Terminuebersicht[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laedt, setzeLaedt] = useState(false);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [arbeitsliste, setzeArbeitsliste] = useState<readonly Benachrichtigt[] | null>(null);

  /*
   * DER EINZELTERMIN IST DER REGELFALL (BUC-01).
   *
   * Termine entstehen einzeln; die Serie ist die Ausnahme fuer wiederkehrende
   * Formate. Die Maske stellt beides in dieser Reihenfolge — wer den haeufigen
   * Fall unter den seltenen stellt, laesst jeden Tag mehrere Leute suchen.
   */
  const [einzelOffen, setzeEinzelOffen] = useState(false);
  const [eBeginn, setzeEBeginn] = useState(() => rastervorschlag(14));
  const [eEnde, setzeEEnde] = useState(() =>
    alsLokal(new Date(new Date(rastervorschlag(14)).getTime() + 4 * 3_600_000)),
  );
  const [eHoechst, setzeEHoechst] = useState('12');
  const [eMindest, setzeEMindest] = useState('4');
  const [eTerminart, setzeETerminart] = useState<'offen' | 'exklusiv'>('offen');
  /*
   * TABELLE 27, ZEILE 2 — GLEICH ZUM VERKAUF FREIGEBEN, VORGABE AN.
   *
   * Bis zum 19.09.2026 blieb jeder hier angelegte Termin „geplant" und damit
   * unsichtbar fuer Portal und Buchungsauswahl (0252). Wer ihn erst spaeter
   * verkaufen will, nimmt den Haken heraus und gibt ihn am Termin frei.
   */
  const [eFreigeben, setzeEFreigeben] = useState(true);
  const [sFreigeben, setzeSFreigeben] = useState(true);
  /* Der Ort der Serie — dieselbe Vorgabe wie beim Einzeltermin (Auftrag #22). */
  const [sOrt, setzeSOrt] = useState('');
  /* BUC-68: der Auftrag, aus dem der Exklusivtermin entsteht. */
  const [eAuftrag, setzeEAuftrag] = useState('');
  const [auftraege, setzeAuftraege] = useState<readonly Auftragszeile[]>([]);
  useEffect(() => {
    if (eTerminart !== 'exklusiv') return;
    let verworfen = false;
    void ladeAuftraege({ status: 'bestaetigt' })
      .then((e) => {
        if (!verworfen) setzeAuftraege(e);
      })
      .catch(() => {
        if (!verworfen) setzeAuftraege([]);
      });
    return () => {
      verworfen = true;
    };
  }, [eTerminart]);
  const [eOrt, setzeEOrt] = useState('');
  const [standorte, setzeStandorte] = useState<
    readonly { readonly kennung: string; readonly bezeichnung: string }[]
  >([]);

  useEffect(() => {
    let verworfen = false;
    ladeStandorte()
      .then((a) => {
        if (!verworfen) setzeStandorte(a.standorte);
      })
      .catch(() => {
        if (!verworfen) setzeStandorte([]);
      });
    return () => {
      verworfen = true;
    };
  }, []);
  const [ePreis, setzeEPreis] = useState('');
  const [eRessourcen, setzeERessourcen] = useState('');
  const [eMehrOffen, setzeEMehrOffen] = useState(false);
  const [eFrueheste, setzeEFrueheste] = useState('');
  const [eSpaeteste, setzeESpaeteste] = useState('');
  /* FRS-04: die Uhrzeit des Buchungsschlusses. Leer heisst 23:59. */
  const [eSchlussUhr, setzeESchlussUhr] = useState('');
  const [eStorno, setzeEStorno] = useState('');
  const [eUmbuchung, setzeEUmbuchung] = useState('');

  /* Die Serie (BUC-08) — die Ausnahme. */
  const [serieOffen, setzeSerieOffen] = useState(false);
  const [ersterBeginn, setzeErsterBeginn] = useState(() => rastervorschlag(14));
  const [dauer, setzeDauer] = useState('240');
  const [anzahl, setzeAnzahl] = useState('4');
  const [hoechst, setzeHoechst] = useState('12');
  const [musterart, setzeMusterart] = useState<'abstand' | 'monatlich'>('abstand');
  const [abstand, setzeAbstand] = useState('7');
  const [wochentag, setzeWochentag] = useState('2');
  const [woche, setzeWoche] = useState('1');

  /* Verlegen und Absagen (ÄA-04). */
  const [vorgang, setzeVorgang] = useState<{ termin: string; art: 'verlegen' | 'absagen' } | null>(
    null,
  );
  const [beginnNeu, setzeBeginnNeu] = useState('');
  const [endeNeu, setzeEndeNeu] = useState('');
  /*
   * EB-DP-07: Die bisherige Dauer des Termins. Das Ende folgt dem gewählten
   * Beginn mit ihr; ein eigenes Ende nur nach ausdrücklichem Umschalten —
   * sonst wird ein Termin, dessen Tag man umstellt, unbemerkt mehrtägig.
   */
  const [dauerBisher, setzeDauerBisher] = useState(0);
  const [dauerAendern, setzeDauerAendern] = useState(false);
  const beginnZeit = new Date(beginnNeu).getTime();
  const endeVerlegt =
    dauerAendern || Number.isNaN(beginnZeit)
      ? endeNeu
      : alsLokal(new Date(beginnZeit + dauerBisher));
  const [grund, setzeGrund] = useState('');

  /**
   * Welcher Termin gemeint ist — als Satz über dem Formular.
   *
   * Im Fenster ist die Zeile nicht mehr zu sehen, aus der man kam. Ohne diese
   * Zeile stünde dort „Termin absagen" und sonst nichts, und der Grund, den
   * jemand eintippt, ginge womöglich an die falschen Leute.
   */
  const betroffenerTermin = (id: string): string | undefined => {
    const t = termine.find((z) => z.id === id);
    return t === undefined ? undefined : new Date(t.beginn).toLocaleString('de-DE');
  };

  useEffect(() => {
    /*
     * `nurWaehlbar` und nicht "nurBuchbare": Die Produktliste kennt den
     * Filter unter diesem Namen, und ein erfundener zweiter Name waere ein
     * stiller Filter, der nichts filtert.
     */
    ladeArtikel({ nurWaehlbar: true })
      .then((gefunden) => {
        setzeArtikel(gefunden.artikel);
      })
      .catch(() => {
        setzeArtikel([]);
      });
  }, []);

  const holen = useCallback(async () => {
    if (gewaehlt === '') {
      setzeTermine([]);
      return;
    }
    setzeLaedt(true);
    try {
      /*
       * DIE VERWALTUNG SIEHT ALLE TERMINE, nicht nur die buchbaren.
       *
       * Hier stand `buchbare(gewaehlt)` — die Buchungsauswahl. Sie zeigt nur
       * offene Termine im Zustand „buchbar", und damit sah diese Maske nie,
       * was sie gerade angelegt hatte: Ein neuer Termin ist „geplant"
       * (Tabelle 27, Zeile 1). Seit dem Filter nach BUC-67 fehlten auch alle
       * Exklusivtermine — gemeldet aus dem Betrieb: Nach der Annahme eines
       * Angebots stand der erzeugte Termin nirgends, obwohl es ihn gab.
       */
      setzeTermine(await alleZumArtikel(gewaehlt));
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [gewaehlt]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const einzelAbsenden = useCallback(
    async (ereignis: FormEvent) => {
      ereignis.preventDefault();
      if (gewaehlt === '') return;
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const zahl = (wert: string): number | undefined =>
          wert.trim() === '' ? undefined : Number(wert);
        await anlegen({
          artikel: gewaehlt,
          beginn: new Date(eBeginn).toISOString(),
          ende: new Date(eEnde).toISOString(),
          hoechstteilnehmer: Number(eHoechst),
          terminart: eTerminart,
          ...(eTerminart === 'exklusiv' && eAuftrag !== ''
            ? { herkunftAuftrag: eAuftrag }
            : {}),
          ...(eMindest.trim() === '' ? {} : { mindestteilnehmer: Number(eMindest) }),
          ...(eOrt.trim() === '' ? {} : { ort: eOrt.trim() }),
          ...(ePreis.trim() === '' ? {} : { preisAbweichend: ePreis.trim() }),
          ...(zahl(eRessourcen) === undefined ? {} : { ressourcenObergrenze: Number(eRessourcen) }),
          ...(zahl(eFrueheste) === undefined ? {} : { fruehesteBuchungTage: Number(eFrueheste) }),
          ...(zahl(eSpaeteste) === undefined ? {} : { spaetesteBuchungTage: Number(eSpaeteste) }),
          ...(zahl(eStorno) === undefined ? {} : { stornofristTage: Number(eStorno) }),
          ...(zahl(eUmbuchung) === undefined ? {} : { umbuchungsfristTage: Number(eUmbuchung) }),
          ...(eSchlussUhr.trim() === '' ? {} : { buchungsschlussUhr: eSchlussUhr.trim() }),
          freigeben: eTerminart === 'offen' && eFreigeben,
        });
        setzeMeldung(
          eTerminart === 'offen' && eFreigeben
            ? 'Der Termin ist angelegt und buchbar.'
            : 'Der Termin ist angelegt — er steht auf „geplant", bis Sie ihn freigeben.',
        );
        setzeEinzelOffen(false);
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [
      gewaehlt, eBeginn, eEnde, eHoechst, eMindest, eTerminart, eOrt, ePreis,
      eRessourcen, eFrueheste, eSpaeteste, eStorno, eUmbuchung, eFreigeben, holen,
    ],
  );

  /** Tabelle 27, Zeile 2: „geplant" → „buchbar". */
  const verkaufFreigeben = useCallback(
    async (termin: string) => {
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const { freigegeben } = await freigebenTermin(termin);
        setzeMeldung(
          freigegeben
            ? 'Der Termin ist zum Verkauf freigegeben — Portal und Buchung zeigen ihn jetzt.'
            : 'Der Termin war nicht mehr „geplant"; es wurde nichts geändert.',
        );
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    },
    [holen],
  );

  const serieAbsenden = useCallback(
    async (ereignis: FormEvent) => {
      ereignis.preventDefault();
      if (gewaehlt === '') return;
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const ergebnis = await serieAnlegen({
          artikel: gewaehlt,
          ersterBeginn: new Date(ersterBeginn).toISOString(),
          dauerMinuten: Number(dauer),
          anzahl: Number(anzahl),
          hoechstteilnehmer: Number(hoechst),
          muster:
            musterart === 'abstand'
              ? { art: 'abstand', tage: Number(abstand) }
              : {
                  art: 'monatlich',
                  wochentag: Number(wochentag),
                  woche: (woche === 'letzte' ? 'letzte' : Number(woche)) as 1 | 2 | 3 | 4 | 'letzte',
                },
          ...(sOrt === '' ? {} : { ort: sOrt }),
          freigeben: sFreigeben,
        });
        setzeMeldung(`${String(ergebnis.termine.length)} Termine angelegt.`);
        setzeSerieOffen(false);
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [
      gewaehlt, ersterBeginn, dauer, anzahl, hoechst, musterart, abstand, wochentag, woche,
      sOrt, sFreigeben, holen,
    ],
  );

  const vorgangAbsenden = useCallback(
    async (ereignis: FormEvent) => {
      ereignis.preventDefault();
      if (vorgang === null) return;
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      setzeArbeitsliste(null);
      try {
        // Die Absage läuft über die Rückfrage (absageBestaetigen); hier nur das Verlegen.
        const ergebnis = await verlegen(vorgang.termin, {
          beginnNeu: new Date(beginnNeu).toISOString(),
          endeNeu: new Date(endeVerlegt).toISOString(),
          // EB-DP-07: Ohne dieses Feld weist der Server eine andere Dauer ab.
          ...(dauerAendern ? { dauerAendern: true } : {}),
          ...(grund.trim() === '' ? {} : { grund: grund.trim() }),
        });
        // ÄW-DP-02: Eine Ablehnung kommt als 409 und landet unten in `fehler`.
        setzeMeldung(
          vorgangsmeldung(
            'Der Termin ist verlegt; Buchungen und Reservierungen sind mitgezogen.',
            ergebnis.angeschrieben,
          ),
        );
        setzeArbeitsliste(ergebnis.angeschrieben);
        setzeVorgang(null);
        setzeGrund('');
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [vorgang, beginnNeu, endeVerlegt, dauerAendern, grund, holen],
  );

  /*
   * DIE ABSAGE FRAGT IM POP-UP NACH (Auftrag CRM-103). Sie benachrichtigt alle
   * Betroffenen sofort; der Pflichtgrund steht deshalb in der Rückfrage selbst,
   * und ein Fehler bleibt dort stehen — auch die Ablehnung eines Termins, der
   * schon abgesagt oder durchgeführt ist (ÄW-DP-02, 409).
   */
  const absageBestaetigen = useCallback(
    async (grundText: string) => {
      if (vorgang === null) return;
      setzeMeldung(null);
      setzeArbeitsliste(null);
      const ergebnis = await absagen(vorgang.termin, grundText);
      setzeMeldung(
        vorgangsmeldung(
          'Der Termin ist abgesagt; die Buchungen stehen als Fälle in der Absageliste.',
          ergebnis.angeschrieben,
        ),
      );
      setzeArbeitsliste(ergebnis.angeschrieben);
      setzeVorgang(null);
      setzeGrund('');
      await holen();
    },
    [vorgang, holen],
  );

  /* BUC-07, CRM-198: Reservierungen ohne Buchung sind keine Buchungen der Arbeitsliste. */
  const buchungen = arbeitsliste?.filter((a) => a.reservierung !== true) ?? [];
  const reservierungen = (arbeitsliste?.length ?? 0) - buchungen.length;
  const offen = buchungen.filter((a) => a.grund !== null);

  /* Standort und Produkt, gewählt in der Werkzeugleiste. */
  const [ort, setzeOrt] = useState('');
  const gewaehlterArtikel = artikel.find((a) => a.id === gewaehlt);
  /*
   * DIE AKTIVEN STANDORTE DES GEWÄHLTEN PRODUKTS (PRD-01, BUC-01 i. d. F.
   * AeA-39, Entwurf, Auftrag #22). Genau einer ist die Vorgabe für den Ort —
   * wie der Auslöser aus 0279 es auf allen Wegen tut. Mehrere stehen zur
   * Wahl, ohne dass einer vorbelegt ist: Welcher gemeint ist, weiß nur, wer
   * den Termin anlegt.
   */
  const produktorte = (gewaehlterArtikel?.standorte ?? []).filter((s) => s.aktiv);

  /* Die Liste folgt dem Standort wie der Kalender — gefiltert wird hier, die Auskunft kommt ungefiltert. */
  const gezeigt = ort === '' ? termine : termine.filter((t) => t.ort === ort);

  /*
   * DIE WERKZEUGLEISTE (ÄA-41, Seitentyp Planung): Ansicht, Produkt, Standort.
   *
   * TAG-29: DREI SICHTEN AUF DIESELBEN TERMINE. Der KALENDER beantwortet
   * „an welchen Tagen laeuft etwas", die TAGE IM EINZELNEN „was laeuft an
   * diesen Tagen, und wann" — beide brauchen kein Produkt. Die LISTE
   * beantwortet „welche Termine hat dieses Produkt" und ist der Weg zum
   * Anlegen, Verlegen und Absagen. Umschaltbar und nicht nebeneinander:
   * Beides zugleich waere auf der Mindestbreite aus Tabelle 19 nicht ohne
   * waagerechtes Scrollen zu bedienen (NFA-22).
   *
   * EINE WAHL MIT DREI MÖGLICHKEITEN und nicht zwei Umschalter (Auftrag
   * CRM-67): „Kalender | Tage im Einzelnen" neben „Kalender | Liste je
   * Produkt" hiesse zweimal „Kalender" für zwei verschiedene Dinge.
   */
  const werkzeuge = (
    <>
      <Segmente<Terminansicht>
        beschriftung="Ansicht"
        optionen={[
          { wert: 'kalender', text: 'Kalender' },
          { wert: 'tage', text: 'Tage im Einzelnen' },
          { wert: 'liste', text: 'Liste je Produkt' },
        ]}
        gewaehlt={sicht}
        beiWahl={setzeSicht}
      />
      <label className="liste__filter">
        Produkt{' '}
        <select
          aria-label="Produkt"
          value={gewaehlt}
          onChange={(e) => {
            setzeGewaehlt(e.target.value);
            setzeArbeitsliste(null);
            // PRD-01, BUC-01 i. d. F. AeA-39 (Entwurf, Auftrag #22): Genau ein
            // aktiver Standort ist die Vorgabe für den Ort, mehrere lassen die
            // Wahl offen. Das Feld bleibt änderbar.
            const orte = (artikel.find((a) => a.id === e.target.value)?.standorte ?? []).filter(
              (s) => s.aktiv,
            );
            setzeEOrt(orte.length === 1 ? orte[0]!.kennung : '');
            setzeSOrt(orte.length === 1 ? orte[0]!.kennung : '');
          }}
        >
          <option value="">— Produkt wählen —</option>
          {artikel.map((a) => (
            <option key={a.id} value={a.id}>
              {a.artikelnummer} — {a.bezeichnung}
            </option>
          ))}
        </select>
      </label>
      <label className="liste__filter">
        Standort{' '}
        <select
          aria-label="Standort"
          value={ort}
          onChange={(e) => {
            setzeOrt(e.target.value);
          }}
        >
          <option value="">Alle Standorte</option>
          {standorte.map((s) => (
            <option key={s.kennung} value={s.kennung}>
              {s.bezeichnung}
            </option>
          ))}
        </select>
      </label>
      {/*
        * DER EINZELTERMIN STEHT VORN (ÄA-41: als Handlung in der Werkzeugleiste,
        * die Formulare öffnen direkt darunter).
        *
        * Termine entstehen in der Regel einzeln; die Serie ist die
        * Ausnahme fuer wiederkehrende Formate. Wer den haeufigen Fall
        * unter den seltenen stellt, laesst jeden Tag mehrere Leute suchen.
        */}
      {sicht === 'liste' && gewaehlt !== '' && darfAnlegen && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => {
              setzeEinzelOffen(!einzelOffen);
              setzeSerieOffen(false);
            }}
          >
            {einzelOffen ? 'Abbrechen' : 'Termin anlegen'}
          </button>
          <button
            type="button"
            className="knopf"
            onClick={() => {
              setzeSerieOffen(!serieOffen);
              setzeEinzelOffen(false);
            }}
          >
            {serieOffen ? 'Abbrechen' : 'Serie anlegen'}
          </button>
        </div>
      )}
    </>
  );

  /* Meldungen beider Ansichten zwischen Werkzeugleiste und Fläche. */
  const oben = (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {/* Tabelle 27, Zeile 9 (CRM-111): was die Orga noch zu bestätigen hat. */}
      {darfVereinbarungAendern && (
        <Durchfuehrungsliste
          darfAbsagen={darfAbsagen}
          darfOptionBeantworten={darfOptionBeantworten}
          darfWartelisteSehen={darfWartelisteSehen}
          {...(beiTerminakte === undefined ? {} : { beiTerminakte })}
        />
      )}

      {/*
       * BUC-138: die Arbeitsliste zur Einzelbehandlung.
       *
       * Sie steht NACH dem Vorgang da, und zwar auch dann, wenn alle
       * angeschrieben wurden — dann sagt sie das. Nur bei einer leeren
       * Rückgabe (kein Betroffener) bleibt sie weg.
       */}
      {arbeitsliste !== null && arbeitsliste.length > 0 && (
        <div className="karte" role="status">
          <h2>Benachrichtigung der Betroffenen</h2>
          {buchungen.length === 0 ? (
            <p>Es war keine Buchung betroffen.</p>
          ) : offen.length === 0 ? (
            <p>
              <span className="marke marke--gut">vollständig</span> Alle{' '}
              {buchungen.length} betroffenen Buchungen sind angeschrieben.
            </p>
          ) : (
            <>
              <p>
                <span className="marke marke--warn">Einzelbehandlung</span>{' '}
                {offen.length} von {buchungen.length} Buchungen wurden{' '}
                <strong>nicht</strong> angeschrieben.
                Diese Kunden erfahren sonst nichts und stehen am alten Datum vor der Tür.
              </p>
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle">
                  <caption className="nur-vorlesen">Nicht angeschriebene Buchungen</caption>
                  <thead>
                    <tr>
                      <th scope="col">Buchung</th>
                      <th scope="col">Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offen.map((a) => (
                      <tr key={a.buchung}>
                        <td className="liste__nummer">{a.buchungsnummer}</td>
                        <td>{a.grund}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {reservierungen > 0 && <p className="hilfe">{reservierungssatz(reservierungen)}</p>}
        </div>
      )}
    </>
  );

  if (sicht !== 'liste') {
    return (
      <Terminkalender
        {...(gewaehlt === '' ? {} : { artikel: gewaehlt })}
        {...(ort === '' ? {} : { ort })}
        ansicht={sicht}
        {...(beiTagesplan === undefined ? {} : { beiTagesplan })}
        werkzeuge={werkzeuge}
        oben={oben}
        darfAbsagen={darfAbsagen}
        darfOptionBeantworten={darfOptionBeantworten}
        darfVereinbarungAendern={darfVereinbarungAendern}
        darfWartelisteSehen={darfWartelisteSehen}
        {...(beiTerminakte === undefined ? {} : { beiTerminakte })}
      />
    );
  }

  /*
   * DIE SEITENLEISTE: DAS GEWÄHLTE PRODUKT (ÄA-41). Was es ist, wo es
   * stattfindet und wie viele seiner Termine noch nicht zum Verkauf
   * freigegeben sind (Tabelle 27, Zeile 1).
   */
  const geplant = termine.filter((t) => t.status === 'geplant').length;
  const seite =
    gewaehlterArtikel === undefined ? undefined : (
      <>
        <div className="karte__titel">
          <h2>{gewaehlterArtikel.bezeichnung}</h2>
          <small>{gewaehlterArtikel.artikelnummer}</small>
        </div>
        <dl className="angaben">
          <dt>Standorte des Produkts</dt>
          <dd>
            {gewaehlterArtikel.standorte.length === 0
              ? 'keiner hinterlegt'
              : gewaehlterArtikel.standorte
                  .map((s) => (s.aktiv ? s.bezeichnung : `${s.bezeichnung} (abgeschaltet)`))
                  .join(', ')}
          </dd>
          <dt>Termine</dt>
          <dd>
            {ort === ''
              ? String(termine.length)
              : `${String(gezeigt.length)} von ${String(termine.length)} an diesem Standort`}
          </dd>
          <dt>Noch nicht freigegeben</dt>
          <dd>{geplant === 0 ? 'keiner' : String(geplant)}</dd>
        </dl>
      </>
    );

  return (
    /*
     * SEITENTYP PLANUNG (ÄA-41): oben die Werkzeugleiste, darunter die Termine
     * des Produkts in voller Breite, rechts das gewählte Produkt.
     */
    <Planung
      werkzeuge={werkzeuge}
      seitenBeschriftung="Gewähltes Produkt"
      {...(seite === undefined ? {} : { seite })}
      oben={
        <>
          {oben}
          {gewaehlt === '' && (
            <p className="hinweiszeile">
              Wählen Sie oben ein Produkt — die Liste zeigt alle seine Termine.
            </p>
          )}
        </>
      }
    >
      {gewaehlt !== '' && (
        <>
          {einzelOffen && (
            <form className="karte" onSubmit={einzelAbsenden}>
              <h2>Neuer Termin</h2>

              <Beginnwahl
                kennung="eBeginn"
                beschriftung="Beginn"
                pflicht
                wert={eBeginn}
                beiAenderung={(neu) => {
                  /*
                   * Das Ende zieht mit, solange es hinter dem Beginn lag.
                   * Ein Ende, das beim Verschieben des Beginns stehen bleibt,
                   * ist der haeufigste Tippfehler dieser Maske — und der
                   * Server weist ihn dann ab.
                   *
                   * NUR BEI GUELTIGEN WERTEN: Waehrend der Eingabe ist das
                   * Datum zwischendurch leer, und `new Date('')` ist NaN.
                   * Daraus ein Ende zu rechnen ergaebe ein "Invalid Date" im
                   * Feld — beim ersten Anlauf genau so passiert.
                   */
                  const alterBeginn = new Date(eBeginn).getTime();
                  const altesEnde = new Date(eEnde).getTime();
                  const neuerBeginn = new Date(neu).getTime();
                  setzeEBeginn(neu);
                  if (
                    Number.isFinite(alterBeginn)
                    && Number.isFinite(altesEnde)
                    && Number.isFinite(neuerBeginn)
                    && altesEnde > alterBeginn
                  ) {
                    setzeEEnde(alsLokal(new Date(neuerBeginn + (altesEnde - alterBeginn))));
                  }
                }}
              />

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eEnde">
                  Ende <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="eEnde"
                  type="datetime-local"
                      step={900}
                  className="feld__eingabe"
                  value={eEnde}
                  onChange={(e) => {
                    setzeEEnde(e.target.value);
                  }}
                />
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eTerminart">
                  Terminart
                </label>
                <select
                  id="eTerminart"
                  className="feld__eingabe"
                  value={eTerminart}
                  onChange={(e) => {
                    setzeETerminart(e.target.value as 'offen' | 'exklusiv');
                  }}
                >
                  <option value="offen">offen — Einzelplatzverkauf</option>
                  <option value="exklusiv">exklusiv — für einen Kunden</option>
                </select>
                {/* BUC-67 */}
                <p className="feld__hilfe">
                  Ein exklusiver Termin ist nicht öffentlich sichtbar und auch nicht
                  buchbar — seine Kapazität gehört dem Kunden ganz.
                </p>
              </div>
              {eTerminart === 'offen' && (
                <label className="feld">
                  <span>
                    <input
                      type="checkbox"
                      checked={eFreigeben}
                      onChange={() => {
                        setzeEFreigeben(!eFreigeben);
                      }}
                    />{' '}
                    Gleich zum Verkauf freigeben
                  </span>
                  <span className="feld__hilfe">
                    Ohne Haken bleibt der Termin „geplant" — Portal und Buchung sehen ihn erst
                    nach der Freigabe am Termin.
                  </span>
                </label>
              )}

              {/*
                BUC-68: „Ein Exklusivtermin ENTSTEHT AUS einem Angebot oder
                Auftrag."

                Ohne diese Angabe wies der CHECK `termin_herkunft` den Termin
                ab, und beim Anwender kam „unerwartet" an — die Maske bot eine
                Wahl an, die zuverlässig scheiterte. Ein Weg, der immer
                fehlschlägt, gehört gar nicht erst angeboten (dieselbe Lehre
                wie beim Artikel ohne Preis, 31.08.2026).
              */}
              {eTerminart === 'exklusiv' && (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="eAuftrag">
                    Auftrag <span className="feld__pflicht">Pflicht</span>
                  </label>
                  <select
                    id="eAuftrag"
                    className="feld__eingabe"
                    value={eAuftrag}
                    onChange={(e) => setzeEAuftrag(e.target.value)}
                  >
                    <option value="">— bitte wählen —</option>
                    {auftraege.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.auftragsnummer} · {a.kundenname} · {a.betreff}
                      </option>
                    ))}
                  </select>
                  {/* ANG-27 */}
                  <p className="feld__hilfe">
                    {auftraege.length === 0
                      ? 'Kein bestätigter Auftrag vorhanden. Der übliche Weg ist umgekehrt: '
                        + 'Nehmen Sie das Angebot an — dann entstehen Termin und '
                        + 'Vereinbarung von selbst.'
                      : 'Der Auftrag bestimmt, wem der Termin gehört. Die Preisgrundlage ist '
                        + 'danach die Exklusivvereinbarung, nicht die Preisliste.'}
                  </p>
                </div>
              )}

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eHoechst">
                  Höchstteilnehmer <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="eHoechst"
                  type="number"
                  min={1}
                  className="feld__eingabe feld__eingabe--schmal"
                  value={eHoechst}
                  onChange={(e) => {
                    setzeEHoechst(e.target.value);
                  }}
                />
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eMindest">
                  Mindestteilnehmer
                </label>
                <input
                  id="eMindest"
                  type="number"
                  min={0}
                  className="feld__eingabe feld__eingabe--schmal"
                  value={eMindest}
                  onChange={(e) => {
                    setzeEMindest(e.target.value);
                  }}
                />
                {/* BUC-02 */}
                <p className="feld__hilfe">
                  Wird sie bis zur Frist nicht erreicht, steht der Termin zur Absage an.
                </p>
              </div>

              <Ortwahl
                kennung="eOrt"
                wert={eOrt}
                beiWahl={setzeEOrt}
                produktorte={produktorte}
                standorte={standorte}
              />

              {/*
                HIER STAND DAS FELD „GRUPPEN" (TAG-04, TAG-05 — entfallen mit
                ÄA-09).

                Der Auftraggeber am 07.09.2026: „Ein Termin wird nie als Serie
                oder Gruppe angelegt." Eine Firma mit dreißig Teilnehmern bucht
                mehrere TERMINE, und die Gruppe gab es als Tabelle, bis
                Migration 0140 sie abgeworfen hat. Das Feld blieb stehen und
                schrieb weiter in `fach.gruppe` — wer mehr als eine Gruppe
                eintrug, bekam einen unerwarteten Fehler (gemeldet am
                10.09.2026).

                Der Hinweis bleibt, weil die Frage bleibt: Wer dreißig
                Teilnehmer unterbringen will, sucht an dieser Stelle danach.
              */}
              {/* E-38, TAG-15 */}
              {eTerminart === 'exklusiv' && (
                <p className="feld__hilfe">
                  Ein Termin ist eine Gruppe und ein Trainer. Überschreitet
                  die gemeldete Teilnehmerzahl die Höchstgruppengröße, legen Sie{' '}
                  <strong>mehrere Termine</strong> an — von Hand oder als Serie. Gruppen
                  innerhalb eines Termins gibt es nicht mehr.
                </p>
              )}

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="ePreis">
                  Abweichender Preis
                </label>
                <input
                  id="ePreis"
                  className="feld__eingabe feld__eingabe--schmal"
                  value={ePreis}
                  placeholder="leer = Preis des Produkts"
                  onChange={(e) => {
                    setzeEPreis(e.target.value);
                  }}
                />
                <p className="feld__hilfe">
                  Leer heißt: Es gilt der Preis des Produkts. Ein Wert hier überschreibt ihn nur
                  für diesen Termin.
                </p>
              </div>

              {/*
                * DIE SELTENEN FELDER STEHEN ZUGEKLAPPT.
                *
                * Ressourcengrenze und die vier Fristen werden bei den meisten
                * Terminen nicht angefasst — sie kommen sonst vom Produkt. Sie
                * offen hinzustellen macht aus einem Formular mit fünf Feldern
                * eines mit elf, und der häufige Fall wird dadurch nicht besser.
                */}
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--verweis"
                  onClick={() => {
                    setzeEMehrOffen(!eMehrOffen);
                  }}
                >
                  {eMehrOffen ? 'Weitere Angaben ausblenden' : 'Weitere Angaben (Grenzen, Fristen)'}
                </button>
              </div>

              {eMehrOffen && (
                <>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eRessourcen">
                      Ressourcenbedingte Obergrenze
                    </label>
                    <input
                      id="eRessourcen"
                      type="number"
                      min={0}
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eRessourcen}
                      onChange={(e) => {
                        setzeERessourcen(e.target.value);
                      }}
                    />
                    <p className="feld__hilfe">
                      Leer heißt: keine. Die maßgebliche Kapazität ist das Minimum aus
                      Höchstteilnehmern, dieser Grenze und der Tagesplanzahl.
                    </p>
                  </div>

                  {/* BUC-04 */}
                  <p className="hilfe">
                    Die vier Fristen sind <strong>Kalendertage vor dem Termin</strong> — nicht
                    Zeitpunkte. So ziehen sie bei einer Verlegung von selbst mit.
                    Leer heißt: Es gilt, was am Produkt steht.
                  </p>
                  <p className="hilfe">
                    Sie enden am Tag „Terminbeginn minus n" zur Uhrzeit des Buchungsschlusses,
                    nicht zur Uhrzeit des Termins: Bei einem Training am 20. März um 8:00 und
                    14 Tagen Frist ist der letzte Zeitpunkt der 6. März um 23:59. Die
                    <strong> früheste</strong> Buchung öffnet dagegen um 00:00 — wer „ab dem
                    20. Dezember" liest, meint den ganzen Tag.
                  </p>

                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eFrueheste">
                      Früheste Buchung (Tage vorher)
                    </label>
                    <input
                      id="eFrueheste"
                      type="number"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eFrueheste}
                      onChange={(e) => {
                        setzeEFrueheste(e.target.value);
                      }}
                    />
                  </div>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eSpaeteste">
                      Späteste Buchung (Tage vorher)
                    </label>
                    <input
                      id="eSpaeteste"
                      type="number"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eSpaeteste}
                      onChange={(e) => {
                        setzeESpaeteste(e.target.value);
                      }}
                    />
                  </div>
                  {/*
                    FRS-04: „zur je Frist konfigurierbaren Uhrzeit; die
                    Voreinstellung ist 23:59 Ortszeit." Ohne dieses Feld
                    stuende die Uhrzeit in der Spaltenvorgabe und waere
                    nirgends zu sehen — eine Festlegung, die wirkt und die
                    niemand findet.
                  */}
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eSchlussUhr">
                      Buchungsschluss um (Uhrzeit, Europe/Berlin)
                    </label>
                    <input
                      id="eSchlussUhr"
                      type="time"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eSchlussUhr}
                      placeholder="23:59"
                      onChange={(e) => {
                        setzeESchlussUhr(e.target.value);
                      }}
                    />
                    <p className="feld__hilfe">Leer heißt 23:59 Uhr.</p>
                  </div>

                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eStorno">
                      Stornofrist (Tage vorher)
                    </label>
                    <input
                      id="eStorno"
                      type="number"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eStorno}
                      onChange={(e) => {
                        setzeEStorno(e.target.value);
                      }}
                    />
                  </div>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="eUmbuchung">
                      Umbuchungsfrist (Tage vorher)
                    </label>
                    <input
                      id="eUmbuchung"
                      type="number"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eUmbuchung}
                      onChange={(e) => {
                        setzeEUmbuchung(e.target.value);
                      }}
                    />
                  </div>
                </>
              )}

              <div className="knopfreihe knopfreihe--abschluss">
                <button
                  type="submit"
                  className="knopf knopf--haupt"
                  disabled={
                    laedt
                    || eHoechst.trim() === ''
                    || new Date(eEnde).getTime() <= new Date(eBeginn).getTime()
                    || Number(eMindest || '0') > Number(eHoechst || '0')
                  }
                >
                  Termin anlegen
                </button>
              </div>

              {/*
                * Die beiden Bedingungen, die den Knopf sperren, stehen als
                * Satz da. Ein Knopf, der ohne Erklaerung aus ist, ist eine
                * Sackgasse (ERR-01).
                */}
              {new Date(eEnde).getTime() <= new Date(eBeginn).getTime() && (
                <p className="feld__fehler-satz">Das Ende muss nach dem Beginn liegen.</p>
              )}
              {/* BUC-02 */}
              {Number(eMindest || '0') > Number(eHoechst || '0') && (
                <p className="feld__fehler-satz">
                  Die Mindestteilnehmerzahl darf die Höchstzahl nicht übersteigen — sonst käme
                  der Termin nie zustande.
                </p>
              )}
            </form>
          )}

          {serieOffen && (
            <form className="karte" onSubmit={serieAbsenden}>
              <h2>Terminserie</h2>
              <Beginnwahl
                kennung="ersterBeginn"
                beschriftung="Erster Beginn"
                wert={ersterBeginn}
                beiAenderung={setzeErsterBeginn}
                hilfe="Jeder Termin der Reihe beginnt zu dieser Uhrzeit."
              />
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="dauer">
                  Dauer in Minuten
                </label>
                <input
                  id="dauer"
                  type="number"
                  min={1}
                  className="feld__eingabe feld__eingabe--schmal"
                  value={dauer}
                  onChange={(e) => {
                    setzeDauer(e.target.value);
                  }}
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="musterart">
                  Wiederholung
                </label>
                <select
                  id="musterart"
                  className="feld__eingabe"
                  value={musterart}
                  onChange={(e) => {
                    setzeMusterart(e.target.value as 'abstand' | 'monatlich');
                  }}
                >
                  <option value="abstand">nach festem Abstand</option>
                  <option value="monatlich">monatlich am Wochentag</option>
                </select>
                <p className="feld__hilfe">
                  {musterart === 'abstand'
                    ? '„Alle vierzehn Tage" ist eine Rechnung.'
                    : '„Jeder erste Dienstag im Monat" ist eine Kalenderregel — zwischen zwei '
                      + 'solchen Terminen liegen mal 28, mal 35 Tage.'}
                </p>
              </div>
              {musterart === 'abstand' ? (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="abstand">
                    Abstand in Tagen
                  </label>
                  <input
                    id="abstand"
                    type="number"
                    min={1}
                    className="feld__eingabe feld__eingabe--schmal"
                    value={abstand}
                    onChange={(e) => {
                      setzeAbstand(e.target.value);
                    }}
                  />
                </div>
              ) : (
                <>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="wochentag">
                      Wochentag
                    </label>
                    <select
                      id="wochentag"
                      className="feld__eingabe"
                      value={wochentag}
                      onChange={(e) => {
                        setzeWochentag(e.target.value);
                      }}
                    >
                      {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map(
                        (name, i) => (
                          <option key={name} value={String(i + 1)}>
                            {name}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="woche">
                      Woche im Monat
                    </label>
                    <select
                      id="woche"
                      className="feld__eingabe"
                      value={woche}
                      onChange={(e) => {
                        setzeWoche(e.target.value);
                      }}
                    >
                      <option value="1">erste</option>
                      <option value="2">zweite</option>
                      <option value="3">dritte</option>
                      <option value="4">vierte</option>
                      <option value="letzte">letzte</option>
                    </select>
                  </div>
                </>
              )}
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="anzahl">
                  Anzahl Termine
                </label>
                <input
                  id="anzahl"
                  type="number"
                  min={1}
                  max={200}
                  className="feld__eingabe feld__eingabe--schmal"
                  value={anzahl}
                  onChange={(e) => {
                    setzeAnzahl(e.target.value);
                  }}
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="hoechst">
                  Höchstteilnehmer
                </label>
                <input
                  id="hoechst"
                  type="number"
                  min={1}
                  className="feld__eingabe feld__eingabe--schmal"
                  value={hoechst}
                  onChange={(e) => {
                    setzeHoechst(e.target.value);
                  }}
                />
              </div>
              <Ortwahl
                kennung="sOrt"
                wert={sOrt}
                beiWahl={setzeSOrt}
                produktorte={produktorte}
                standorte={standorte}
              />
              <label className="feld">
                <span>
                  <input
                    type="checkbox"
                    checked={sFreigeben}
                    onChange={() => {
                      setzeSFreigeben(!sFreigeben);
                    }}
                  />{' '}
                  Jeden Termin gleich zum Verkauf freigeben
                </span>
              </label>
              <div className="knopfreihe knopfreihe--abschluss">
                <button type="submit" className="knopf knopf--haupt" disabled={laedt}>
                  Serie anlegen
                </button>
              </div>
            </form>
          )}

          <div className="karte">
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption className="nur-vorlesen">Alle Termine dieses Produkts</caption>
                <thead>
                  <tr>
                    <th scope="col">Beginn</th>
                    <th scope="col">Ende</th>
                    <th scope="col">Art</th>
                    <th scope="col">Frei</th>
                    <th scope="col">Status</th>
                    <th scope="col"> </th>
                  </tr>
                </thead>
                <tbody>
                  {gezeigt.map((t) => (
                    <tr key={t.id}>
                      <td>
                        {/*
                          DER BEGINN FÜHRT INS FENSTER (Auftraggeber, 13.09.2026:
                          „jeder Termin auswählbar"). Verlegen und Absagen bleiben
                          daneben stehen: Wer eine Reihe von Terminen umlegt, will
                          nicht je Termin ein Fenster öffnen.
                        */}
                        <button
                          type="button"
                          className="knopf knopf--verweis liste__einzeilig"
                          onClick={() => {
                            setzeOffenenTermin(t.id);
                          }}
                        >
                          {alsZeitpunkt(t.beginn)}
                        </button>
                      </td>
                      <td className="liste__einzeilig">
                        {new Date(t.ende).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td>
                        {/*
                          BUC-66: die Terminart. Sie stand nirgends, und dem
                          Exklusivtermin sah man deshalb nicht an, dass er
                          einem Kunden ganz gehört — er stand einfach nicht in
                          der Liste, weil sie die Buchungsauswahl war.
  
                          BEIDE WERTE STEHEN GLEICH DA — als Wort in der Spalte
                          und nicht als Marke. „exklusiv" trug einen Kasten,
                          „offen" nicht; das las sich, als sei das eine ein
                          Zustand und das andere keiner. Es sind zwei Werte
                          DESSELBEN Merkmals, und keiner davon wertet: Ein
                          Exklusivtermin ist nicht besser als ein offener.
  
                          Die Marke bleibt dem vorbehalten, was WERTET — dem
                          Status in der Spalte daneben.
                        */}
                        {t.terminart === 'exklusiv' ? 'exklusiv' : 'offen'}
                      </td>
                      <td>
                        {/*
                          KAP-22: die ANONYME Sicht — Platzzahl minus vergebene
                          Haltungen. Die MASSGEBLICHE freie Kapazität ist das
                          Minimum aus drei Grenzen (KAP-03) und hängt am
                          Anfragenden (KAP-04); sie steht in der
                          Buchungsstrecke, nicht in der Planung.
                        */}
                        {t.terminart === 'exklusiv'
                          ? '—'
                          : `${String(t.hoechstteilnehmer - t.belegt)} von ${String(t.hoechstteilnehmer)}`}
                      </td>
                      <td>
                        <span
                          className={
                            t.status === 'buchbar' ? 'marke marke--gut' : 'marke marke--inaktiv'
                          }
                        >
                          {t.status}
                        </span>
                        {/* Tabelle 27, Zeile 2: die Verkaufsfreigabe (0252). */}
                        {t.status === 'geplant' && darfVereinbarungAendern && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="knopf knopf--klein knopf--haupt"
                              onClick={() => void verkaufFreigeben(t.id)}
                            >
                              Zum Verkauf freigeben
                            </button>
                          </>
                        )}
                      </td>
                      <td>
                        {/* ÄW-DP-02, STA-01: nur, wo Tabelle 27 eine Absage kennt. */}
                        {darfVerlegen && verlegbar(t.status) && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeVorgang({ termin: t.id, art: 'verlegen' });
                              setzeBeginnNeu(alsLokal(new Date(t.beginn)));
                              setzeEndeNeu(alsLokal(new Date(t.ende)));
                              setzeDauerBisher(Date.parse(t.ende) - Date.parse(t.beginn));
                              setzeDauerAendern(false);
                              setzeGrund('');
                              setzeArbeitsliste(null);
                            }}
                          >
                            Verlegen
                          </button>
                        )}{' '}
                        {darfAbsagen && verlegbar(t.status) && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeVorgang({ termin: t.id, art: 'absagen' });
                              setzeGrund('');
                              setzeArbeitsliste(null);
                            }}
                          >
                            Absagen …
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {termine.length === 0 && !laedt && (
              <div className="liste__leer" role="status">
                Für dieses Produkt gibt es keinen buchbaren Termin.
              </div>
            )}
            {termine.length > 0 && gezeigt.length === 0 && !laedt && (
              <div className="liste__leer" role="status">
                An diesem Standort hat dieses Produkt keinen Termin — die übrigen zeigt „alle
                Standorte".
              </div>
            )}
          </div>

          {/*
            EIN EINGRIFF GEHÖRT INS FENSTER.

            Verlegen nimmt Buchungen samt Teilnehmern und Reservierungen mit;
            Ressourcenbelegungen, Planeinträge, Freigabe und Zusagen bleiben
            noch auf der alten Zeit (ÄW-DP-34, AP-DP-04/05). Die Absage
            benachrichtigt die Kunden, legt ihre Buchungen als Fälle in die
            Absageliste (BUC-129, BUC-131) und sagt die Einsätze am Termin ab.
            Beides schreibt die Betroffenen an. Ein
            Formular dafür unter einer langen Terminliste ist zweimal falsch:
            Wer die untere Zeile anfasst, sieht es nicht — und wer es sieht,
            weiß beim Ausfüllen nicht mehr sicher, welcher Termin gemeint war.
          */}
          {vorgang?.art === 'absagen' && (
            <Rueckfrage
              frage="Termin absagen?"
              unterzeile={betroffenerTermin(vorgang.termin)}
              tat="Absagen"
              grund={{
                beschriftung: 'Grund',
                hilfe:
                  'Er steht im Anschreiben. „Ihr Termin wurde abgesagt" ohne Grund ist die '
                  + 'Nachricht, nach der jeder Empfänger anruft.',
              }}
              beiSchliessen={() => {
                setzeVorgang(null);
              }}
              beiBestaetigen={absageBestaetigen}
            >
              <p>
                Die Kunden werden sofort benachrichtigt; der Grund steht im Anschreiben. Ihre
                Buchungen erscheinen als Fälle in der Absageliste (Buchung → Absagen) und
                werden dort umgebucht oder erstattet. Die Einsätze am Termin werden abgesagt.
                Die Absage lässt sich nicht zurücknehmen.
              </p>
            </Rueckfrage>
          )}
          {vorgang?.art === 'verlegen' && (
            <Dialog
              titel="Termin verlegen"
              unterzeile={betroffenerTermin(vorgang.termin)}
              beiSchliessen={() => {
                setzeVorgang(null);
              }}
            >
            <form onSubmit={vorgangAbsenden}>
              <p className="hilfe">
                Buchungen samt Teilnehmern und Reservierungen ziehen mit; alle betroffenen
                Kunden werden in einem Vorgang benachrichtigt. Ressourcenbelegungen,
                Einträge der Trainingsplanung, die Freigabe des Tages und Zusagen bleiben auf
                der alten Zeit — prüfen Sie sie nach dem Verlegen.
              </p>

              {/*
                AeA-26: NUR DIE DREIVIERTELSTUNDEN AB ACHT. „In den
                eingabemasken für den beginn soll auch nur diese Auswahl
                möglich sein" (Auftraggeber, 15.09.2026) — deshalb eine
                Liste und kein Feld, in das sich jede Minute tippen lässt.
              */}
              <Beginnwahl
                kennung="beginnNeu"
                beschriftung="Neuer Beginn"
                pflicht
                wert={beginnNeu}
                beiAenderung={setzeBeginnNeu}
              />
              {/*
                EB-DP-07: Das Ende folgt dem Beginn mit gleicher Dauer. Erst
                der Schalter gibt das Feld frei — und schickt dauerAendern
                mit, ohne das der Server eine andere Dauer abweist.
              */}
              <label className="liste__schalter">
                <input
                  type="checkbox"
                  checked={dauerAendern}
                  onChange={(e) => {
                    setzeDauerAendern(e.target.checked);
                    setzeEndeNeu(endeVerlegt);
                  }}
                />
                Andere Dauer
              </label>
              {dauerAendern ? (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="endeNeu">
                    Neues Ende <span className="feld__pflicht">Pflicht</span>
                  </label>
                  <input
                    id="endeNeu"
                    type="datetime-local"
                    step={900}
                    className="feld__eingabe"
                    value={endeNeu}
                    onChange={(e) => {
                      setzeEndeNeu(e.target.value);
                    }}
                  />
                </div>
              ) : (
                <p className="hilfe">
                  Ende {Number.isNaN(beginnZeit) ? '—' : new Date(endeVerlegt).toLocaleString('de-DE')} ·
                  gleiche Dauer wie bisher
                </p>
              )}

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="grund">
                  Grund
                </label>
                <input
                  id="grund"
                  className="feld__eingabe"
                  value={grund}
                  onChange={(e) => {
                    setzeGrund(e.target.value);
                  }}
                />
              </div>

              <div className="knopfreihe knopfreihe--abschluss">
                <button
                  type="button"
                  className="knopf"
                  onClick={() => {
                    setzeVorgang(null);
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="knopf knopf--haupt"
                  disabled={laedt}
                >
                  Verlegen
                </button>
              </div>
            </form>
            </Dialog>
          )}
        </>
      )}

      {offenerTermin !== null && (
        <Terminfenster
          termin={offenerTermin}
          darfAbsagen={darfAbsagen}
          darfOptionBeantworten={darfOptionBeantworten}
          darfVereinbarungAendern={darfVereinbarungAendern}
          darfWartelisteSehen={darfWartelisteSehen}
          {...(beiTerminakte === undefined ? {} : { beiAkte: beiTerminakte })}
          beiSchliessen={() => {
            setzeOffenenTermin(null);
          }}
          beiAenderung={() => {
            void holen();
          }}
        />
      )}
    </Planung>
  );
}
